import { useMemo, ReactNode } from "react";
import { HTTP } from "@/core/http";
import { authStorageService } from "@/modules/auth/services/authStorageService";
import { cognitoAuthService } from "@/modules/auth/services/cognitoAuthService";
import { services } from "./services";
import { ServiceLocatorContext, type ServiceMap } from "./context";
import config from "@/config";

interface ServiceLocatorProviderProps {
  children: ReactNode;
}

export function ServiceLocatorProvider({
  children,
}: ServiceLocatorProviderProps) {
  // Initialize HTTP client
  const httpClient = useMemo(() => {
    const client = new HTTP(config.API_BASE_URL);

    // Set auth token getter
    client.setAuthTokenGetter(() => authStorageService.getToken());

    // Set tenant id getter (x-tenantid)
    client.setTenantIdGetter(() => authStorageService.getTenantId());

    // Set unauthorized handler — await Cognito sign-out before reload so
    // restoreSession cannot immediately re-authenticate and loop.
    let handlingUnauthorized = false;
    client.setUnauthorizedHandler(() => {
      if (handlingUnauthorized) return;
      handlingUnauthorized = true;

      void (async () => {
        try {
          authStorageService.markForceLogout();
          await cognitoAuthService.signOut();
        } finally {
          authStorageService.clear();
          window.location.href = "/auth";
        }
      })();
    });

    return client;
  }, []);

  // Initialize services
  const serviceMap = useMemo(() => {
    const context = { httpClient };
    const map = {} as { [K in keyof ServiceMap]?: ServiceMap[K] };

    for (const serviceSpec of services) {
      const serviceName = serviceSpec.name as keyof ServiceMap;
      // TypeScript doesn't know that we're initializing all services,
      // so we use a type assertion here
      (map as any)[serviceName] = serviceSpec.init(context);
    }

    return map as ServiceMap;
  }, [httpClient]);

  const value = useMemo(
    () => ({
      services: serviceMap,
      httpClient,
    }),
    [serviceMap, httpClient]
  );

  return (
    <ServiceLocatorContext.Provider value={value}>
      {children}
    </ServiceLocatorContext.Provider>
  );
}
